"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import * as React from "react";

import { cn } from "@/common/helpers/utils";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = ({ className, ref, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "data-[state=open]:animate-in data-[state=closed]:animate-out fixed inset-0 z-50 bg-black/60 backdrop-blur-sm",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 duration-300 ease-out",
      className,
    )}
    {...props}
  />
);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = ({
  className,
  children,
  onClose,
  onOverlayClicked,
  showCloseButton = true,
  overlayClassName,
  ref,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  onOverlayClicked?: () => void;
  onClose?: () => void;
  showCloseButton?: boolean;
  overlayClassName?: string;
}) => (
  <DialogPortal>
    <DialogOverlay data-testid="dialog-overlay" className={overlayClassName} onClick={onOverlayClicked} />
    <DialogPrimitive.Content
      ref={ref}
      data-testid="dialog-content"
      data-focus-indicator="none"
      className={cn(
        "fixed top-[50%] left-[50%] z-50 grid w-[calc(100vw-2rem)] max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4",
        "border-border bg-background border p-4 shadow-2xl backdrop-blur-md sm:p-5 dark:shadow-none",
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 duration-300 ease-out",
        "data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 rounded-xl sm:rounded-2xl",
        "max-h-[calc(100vh-2rem)] overflow-y-auto sm:max-w-xl md:max-w-2xl",
        className,
      )}
      onKeyUp={(e) => {
        if (e.key === "Escape") {
          onClose?.();
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close
          data-testid="dialog-close"
          tabIndex={-1}
          className={cn(
            "ring-offset-background absolute top-3 right-3 rounded-full p-1 opacity-70 sm:top-4 sm:right-4 sm:p-1.5",
            "hover:bg-accent transition-all duration-200 hover:scale-110 hover:opacity-100",
            "data-[state=open]:bg-accent disabled:pointer-events-none disabled:opacity-50",
            "data-[state=open]:text-muted-foreground cursor-pointer",
          )}
          onClick={onClose}
        >
          <X className="h-4 w-4 sm:h-5 sm:w-5" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-2 pb-2 text-center sm:text-left", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col-reverse gap-2 pt-4 sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = ({ className, ref, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-foreground text-xl leading-tight font-semibold tracking-tight sm:text-2xl", className)}
    {...props}
  />
);
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = ({ className, ref, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-muted-foreground text-sm leading-relaxed sm:text-base", className)}
    {...props}
  />
);
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
